import type { List } from '@design-system/components/Header/Menu/HeaderMenuNav.props'

/**
 * A composable for fetching header configuration data based on website and locale.
 *
 * Retrieves menu navigation configuration dynamically using the current website
 * and country/language combination.
 *
 * @param {Ref<string>} currentWebsite - Reactive reference to the current website
 * @param {Ref<string>} countryAndLanguage - Reactive reference to the country and language
 *
 * @returns {Promise<UseFetchReturn<List[]>>} A promise resolving to the header menu configuration
 *
 * @example
 * // Fetch header config for a specific website and locale
 * const { data, pending, error } = await useHeaderConfig(
 *   ref('armani'),
 *   ref('us-en')
 * )
 *
 * @remarks
 * - Uses Nuxt's useFetch for data retrieval
 * - Dynamically constructs API endpoint based on inputs
 * - Returns a standard useFetch response with data, pending, and error states
 */
export const useHeaderConfig = async (
  currentWebsite: Ref<string>,
  countryAndLanguage: Ref<string>
) => {
  /**
   * Computed URL for fetching header configuration
   * Combines website and locale to create a dynamic API endpoint
   */
  const url = computed(
    () => `/api/${currentWebsite.value}/${countryAndLanguage.value}`
  )

  /**
   * Fetch header configuration data from the constructed URL
   * @type {List[]} Array of header menu navigation configurations
   */
  return await useFetch<List[]>(url)
}
